import {
  FLOW_PHASES,
  FLOW_STEPS,
  milestoneText,
  phaseIndexOfStep,
  type SubmissionFlowStep,
} from "@/lib/submissionFlow";
import signalMark from "@/assets/signal-mark.svg";

// BRK-S058 — the submission-flow progress stepper, hybrid redesign (Aaron's
// pick over the old six-numbered-circles bar, which read as six disjoint
// screens even though steps 4–6 all live on the one pricing view). Three parts,
// all derived PURELY from the caller-supplied `current` step (itself derived
// from server state via deriveFlowStep — NN #9, no client-stored step):
//
//   1. a three-PHASE spine (Set up · Shop the market · Firm up & bind) that maps
//      to how a broker actually thinks about the flow;
//   2. the ACTIVE phase's concrete sub-steps as a compact done/next checklist;
//   3. an honest milestone line ("N steps to your first price indication") —
//      remaining STEPS, never fabricated minutes (we have no timing data).
//
// Accessibility: the full ordered step path stays in the DOM — the active
// phase's steps are visible in the checklist, the rest are `sr-only` — so a
// screen reader hears the whole path in order and the current step carries
// `aria-current="step"`. The phase spine is decorative (`aria-hidden`); its
// meaning is already conveyed by the active-phase heading + the step list.
export function SubmissionFlowProgress({
  current,
}: {
  current: SubmissionFlowStep;
}) {
  const activePhase = phaseIndexOfStep(current);

  return (
    <nav
      aria-label="Submission progress"
      className="flex w-full flex-col gap-6"
    >
      {/* 1. Phase spine — numbered circles + connectors. Decorative: the step
          list below is the accessible source of truth. */}
      <ol aria-hidden="true" className="flex w-full items-center pb-6">
        {FLOW_PHASES.map((phase, i) => {
          const done = i < activePhase;
          const active = i === activePhase;
          const isLast = i === FLOW_PHASES.length - 1;
          return (
            <li
              key={phase.key}
              className={`flex items-center ${isLast ? "" : "flex-1"}`}
            >
              <div className="relative flex h-9 items-center justify-center">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                    done || active
                      ? "bg-brk-accent text-white"
                      : "border-2 border-brk-border bg-brk-panel text-brk-dim"
                  }`}
                >
                  {done ? "✓" : i + 1}
                </span>
                <span
                  className={`absolute left-1/2 top-11 -translate-x-1/2 whitespace-nowrap text-xs ${
                    active
                      ? "font-semibold text-brk-text"
                      : done
                        ? "text-brk-accent"
                        : "text-brk-dim"
                  }`}
                >
                  {phase.label}
                </span>
              </div>
              {!isLast && (
                <span
                  className={`mx-2 h-0.5 flex-1 rounded ${
                    done ? "bg-brk-accent" : "bg-brk-border"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* 2. Active phase + its sub-steps, with a decorative accent.
          Visually hidden (sr-only) but kept in the DOM: it's the accessible
          source of truth for the flow — the phase circles above are aria-hidden,
          so this carries the ordered step path, aria-current, and the milestone
          for screen readers. Hidden per design; do not delete. */}
      <div className="sr-only">
        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold text-brk-text">
            {FLOW_PHASES[activePhase].label}
          </p>
          <ol className="flex flex-col gap-1.5">
            {FLOW_STEPS.map((s) => {
              const inActivePhase = phaseIndexOfStep(s.step) === activePhase;
              const done = s.step < current;
              const active = s.step === current;
              return (
                <li
                  key={s.step}
                  aria-current={active ? "step" : undefined}
                  className={
                    inActivePhase
                      ? "flex items-center gap-2.5 text-sm"
                      : "sr-only"
                  }
                >
                  <StepMarker done={done} active={active} />
                  <span
                    className={
                      active ? "font-medium text-brk-text" : "text-brk-dim"
                    }
                  >
                    <span className="sr-only">{`Step ${s.step} of ${FLOW_STEPS.length}: `}</span>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          {/* 3. Honest milestone — remaining steps, not minutes. */}
          <p className="mt-1 text-xs text-brk-dim">{milestoneText(current)}</p>
        </div>

        {/* Decorative accent (aria-hidden): the mark + a mini checklist. */}
        <div
          aria-hidden="true"
          className="hidden items-center gap-3 pt-1 sm:flex"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-brk-accent/40 bg-brk-accent/10">
            <img src={signalMark} alt="" className="h-8 w-8" />
          </span>
          <div className="flex flex-col gap-2 rounded-lg border border-brk-border bg-brk-bg p-3">
            {[36, 28, 32].map((w, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-[3px] bg-brk-accent/15 text-[0.55rem] font-bold text-brk-accent">
                  ✓
                </span>
                <span
                  className="h-1.5 rounded-full bg-brk-border"
                  style={{ width: w }}
                />
              </span>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

// The per-sub-step marker: a check when done, a filled ring when active, a
// hollow dot when upcoming. Decorative — the label carries the meaning.
function StepMarker({ done, active }: { done: boolean; active: boolean }) {
  if (done) {
    return (
      <span
        aria-hidden="true"
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brk-accent text-[0.6rem] font-bold text-white"
      >
        ✓
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
        active ? "border-brk-accent" : "border-brk-border"
      }`}
    >
      {active && <span className="h-1.5 w-1.5 rounded-full bg-brk-accent" />}
    </span>
  );
}
